import React, { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import { FiCheckCircle, FiClock, FiX } from "react-icons/fi";
import { TicketGroupResponse, userService } from "../../services/userService";

interface TicketEntry {
	orderItemId: number;
	ticketId: number;
	qrToken: string;
	orderQrToken: string;
	sequenceNumber: number;
	status: "valid" | "redeemed";
}

interface TicketQrModalProps {
	tickets: TicketEntry[];
	initialIndex?: number;
	onClose: () => void;
	eventTitle: string;
	eventLocation?: string;
	eventImageUrl?: string | null;
	scheduleLabel?: string;
	ticketType: string;
	onStatusChange?: (payload: TicketGroupResponse) => void;
}

const POLL_INTERVAL = 4000;

const TicketQrModal: React.FC<TicketQrModalProps> = ({
	tickets,
	initialIndex = 0,
	onClose,
	eventTitle,
	eventLocation,
	eventImageUrl,
	scheduleLabel,
	ticketType,
	onStatusChange,
}) => {
	const [activeIndex, setActiveIndex] = useState(initialIndex);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
	const touchStartXRef = useRef<number | null>(null);

const aggregateTotalCount = tickets.length;
const aggregateUnusedCount = tickets.filter(
	(t) => t.status === "valid"
).length;
const currentTicket = tickets[activeIndex];
const currentToken = currentTicket?.qrToken;
const currentGroupToken = currentTicket?.orderQrToken;
const isCurrentTicketRedeemed = currentTicket?.status === "redeemed";

	useEffect(() => {
		if (tickets.length === 0) {
			setActiveIndex(0);
			return;
		}
		setActiveIndex((prev) => {
			const nextIndex = Math.min(initialIndex, tickets.length - 1);
			return Math.min(prev, tickets.length - 1, nextIndex);
		});
	}, [initialIndex, tickets.length]);

	useEffect(() => {
		let mounted = true;
		let interval: NodeJS.Timeout;

		const fetchData = async (silent = false) => {
			if (!currentGroupToken) return;
			try {
				if (!silent) {
					setLoading(true);
				}
				const response = await userService.getTicketGroup(currentGroupToken);
				if (!mounted) return;
				setLastUpdated(new Date());
				setError(null);
				onStatusChange?.(response);
			} catch (err) {
				if (!mounted) return;
				setError("チケット情報の取得に失敗しました");
			} finally {
				if (!mounted) return;
				if (!silent) {
					setLoading(false);
				}
			}
		};

		fetchData();
		if (currentGroupToken) {
			interval = setInterval(() => fetchData(true), POLL_INTERVAL);
		}

		return () => {
			mounted = false;
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [currentGroupToken, initialIndex, onStatusChange]);

	const qrPayload = useMemo(
		() => (currentToken ? `BPTICKET:${currentToken}` : ""),
		[currentToken]
	);
	const renderTicketStatus = () => {
		if (!tickets.length) {
			return null;
		}

		return (
			<div className="mt-4 max-h-64 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700">
				{tickets.map((ticket, index) => {
					const isValid = ticket.status === "valid";
					return (
						<button
							key={`${ticket.ticketId}-${ticket.sequenceNumber}`}
							onClick={() => setActiveIndex(index)}
							className={`flex w-full items-center justify-between px-4 py-3 border-b border-gray-100 last:border-b-0 dark:border-gray-700 text-left transition-colors ${
								activeIndex === index
									? "bg-blue-50/60 dark:bg-blue-900/20"
									: "hover:bg-gray-50 dark:hover:bg-gray-800"
							}`}
						>
							<div>
								<p className="text-sm font-semibold text-gray-900 dark:text-white">
									#{ticket.sequenceNumber.toString().padStart(3, "0")}（QR{" "}
									{index + 1}）
								</p>
								<p className="text-xs text-gray-500 dark:text-gray-400">
									{isValid ? "未使用" : "使用済み"}
								</p>
							</div>
							<div>
								<span
									className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full ${
										isValid
											? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200"
											: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
									}`}
								>
									{isValid ? "Valid" : "Used"}
								</span>
							</div>
						</button>
					);
				})}
			</div>
		);
	};

	if (!currentTicket) {
		return null;
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-6">
			<div className="relative w-full max-w-3xl rounded-3xl bg-white shadow-2xl dark:bg-gray-900">
				<button
					onClick={onClose}
					className="absolute right-4 top-4 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800"
					aria-label="閉じる"
				>
					<FiX className="h-5 w-5" />
				</button>
				<div className="grid gap-8 p-6 lg:grid-cols-2">
					<div className="space-y-4">
						<div>
							<h3 className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
								{eventTitle}
							</h3>
							<p className="text-sm text-gray-500 dark:text-gray-400">
								{ticketType}
							</p>
							{eventLocation && (
								<p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
									{eventLocation}
								</p>
							)}
							{scheduleLabel && (
								<p className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
									<FiClock /> {scheduleLabel}
								</p>
							)}
						</div>
						{eventImageUrl && (
							<div className="overflow-hidden rounded-2xl border border-gray-100 dark:border-gray-800">
								<img
									src={eventImageUrl}
									alt={eventTitle}
									className="h-40 w-full object-cover"
								/>
							</div>
						)}
						<div className="rounded-2xl bg-gray-50 p-4 dark:bg-gray-800">
							<div className="flex items-center justify-between">
								<div>
									<p className="text-xs uppercase tracking-wide text-gray-500">
										未使用枚数（このチケット種別全体）
									</p>
									<p className="text-2xl font-bold text-gray-900 dark:text-white">
										{aggregateUnusedCount}
										<span className="text-base font-medium text-gray-500">
											{" "}
											/ {aggregateTotalCount}
										</span>
									</p>
								</div>
								<div className="text-right text-xs text-gray-500 dark:text-gray-400">
									{lastUpdated && (
										<p>最終更新: {lastUpdated.toLocaleTimeString()}</p>
									)}
								</div>
							</div>
						</div>
						{renderTicketStatus()}
					</div>

					<div className="flex flex-col items-center justify-center space-y-4 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 p-6 dark:from-gray-800 dark:to-gray-800">
						{tickets.length > 1 && (
							<div className="flex flex-wrap justify-center gap-2">
								{tickets.map((ticket, idx) => (
									<button
										key={`${ticket.ticketId}-${ticket.sequenceNumber}`}
										onClick={() => setActiveIndex(idx)}
										className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
											activeIndex === idx
												? "border-blue-500 bg-white text-blue-600"
												: "border-transparent bg-white/60 text-gray-500"
										}`}
									>
										QR {idx + 1}
									</button>
								))}
							</div>
						)}
						<p className="text-sm font-medium text-blue-700 dark:text-blue-200">
							係員にこのQRコードを提示してください
						</p>
						<div
							className="relative rounded-3xl bg-white p-6 shadow-xl"
							onTouchStart={(e) => {
								if (tickets.length <= 1) return;
								touchStartXRef.current = e.touches[0].clientX;
							}}
							onTouchEnd={(e) => {
								if (tickets.length <= 1) return;
								const startX = touchStartXRef.current;
								if (startX === null) return;
								const diff = e.changedTouches[0].clientX - startX;
								const threshold = 40;
								if (Math.abs(diff) > threshold) {
									if (diff < 0) {
										setActiveIndex((prev) => (prev + 1) % tickets.length);
									} else {
										setActiveIndex(
											(prev) => (prev - 1 + tickets.length) % tickets.length
										);
									}
								}
								touchStartXRef.current = null;
							}}
						>
							<div
								className={`transition-opacity duration-300 ${
									isCurrentTicketRedeemed ? "opacity-40" : "opacity-100"
								}`}
							>
								{!loading && !error ? (
									<QRCode
										value={qrPayload}
										size={220}
										fgColor="#111827"
										bgColor="#ffffff"
									/>
								) : (
									<div className="flex h-[220px] w-[220px] items-center justify-center text-gray-400">
										{error ? "読み込みエラー" : "読み込み中..."}
									</div>
								)}
							</div>
							{isCurrentTicketRedeemed && (
								<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
									<div className="flex h-28 w-28 items-center justify-center rounded-full bg-white/90 text-emerald-600 shadow-xl">
										<FiCheckCircle className="h-14 w-14" />
									</div>
								</div>
							)}
						</div>
						{error && (
							<div className="flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-xs text-red-600">
								<span>{error}</span>
								<button
									onClick={() => setError(null)}
									className="text-red-700 underline"
								>
									閉じる
								</button>
							</div>
						)}
						<button
							onClick={onClose}
							className="w-full rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30"
						>
							閉じる
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TicketQrModal;
